import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { Checkbox as RCheckbox, Switch as RSwitch } from 'radix-ui';
import { motion, useReducedMotion } from 'motion/react';
import { Check, Eye, EyeOff, Minus, Plus, Search, X } from 'lucide-react';
import { digitsToCents, formatAmount, formatCEP, formatDocument, formatPhone, onlyDigits } from '@dezza/shared';
import { cn } from '../lib/cn';
import { springs } from '../lib/motion';

const fieldBase = cn(
  'w-full rounded-control border bg-surface px-3.5 text-[15px] text-ink shadow-control placeholder:text-ink-3',
  'transition-[border-color,box-shadow,background-color] duration-150 ease-standard',
  'focus:outline-none focus-visible:outline-none focus:ring-[3px]',
  'disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-ink-3 disabled:shadow-none',
);
const fieldOk = 'border-line-strong hover:border-ink/25 focus:border-primary focus:ring-primary/20 focus:hover:border-primary';
const fieldError = 'border-danger focus:border-danger focus:ring-danger/20';

// ---------------------------------------------------------------- Label / Field
export function Label({ children, htmlFor, required, className }: { children: ReactNode; htmlFor?: string; required?: boolean; className?: string }) {
  return (
    <label htmlFor={htmlFor} className={cn('mb-1.5 block text-sm font-medium text-ink', className)}>
      {children}
      {required ? (
        <span className="ml-0.5 text-danger" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  );
}

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => ReactNode;
}

/** Envolve um controle com rótulo, dica e mensagem de erro acessíveis. */
export function Field({ label, hint, error, required, className, children }: FieldProps) {
  const id = useId();
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined;
  return (
    <div className={className}>
      {label ? (
        <Label htmlFor={id} required={required}>
          {label}
        </Label>
      ) : null}
      {children({ id, 'aria-invalid': error ? true : undefined, 'aria-describedby': describedBy })}
      {error ? (
        <p id={`${id}-err`} className="mt-1.5 animate-fade-in text-[13px] font-medium text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1.5 text-[13px] text-ink-3">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Input
export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  inputSize?: 'md' | 'lg';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, invalid, leading, trailing, inputSize = 'md', ...props },
  ref,
) {
  const input = (
    <input
      ref={ref}
      className={cn(
        fieldBase,
        invalid || props['aria-invalid'] ? fieldError : fieldOk,
        inputSize === 'lg' ? 'h-12' : 'h-11',
        leading ? 'pl-10' : '',
        trailing ? 'pr-10' : '',
        !leading && !trailing ? className : '',
      )}
      {...props}
    />
  );
  if (!leading && !trailing) return input;
  return (
    <div className={cn('relative', className)}>
      {leading ? (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-ink-3 [&_svg]:size-[18px]">{leading}</span>
      ) : null}
      {input}
      {trailing ? <span className="absolute inset-y-0 right-2 flex items-center text-ink-3">{trailing}</span> : null}
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(function Textarea(
  { className, invalid, rows = 4, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cn(fieldBase, invalid || props['aria-invalid'] ? fieldError : fieldOk, 'min-h-24 py-2.5 leading-relaxed', className)}
      {...props}
    />
  );
});

export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputProps, 'type' | 'trailing'>>(function PasswordInput(props, ref) {
  const [show, setShow] = useState(false);
  return (
    <Input
      ref={ref}
      type={show ? 'text' : 'password'}
      autoComplete={props.autoComplete ?? 'current-password'}
      trailing={
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="rounded-lg p-1.5 text-ink-3 transition-colors hover:bg-ink/[0.06] hover:text-ink"
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {show ? <EyeOff className="size-[18px]" /> : <Eye className="size-[18px]" />}
        </button>
      }
      {...props}
    />
  );
});

// ---------------------------------------------------------------- Dinheiro
export interface MoneyInputProps extends Omit<InputProps, 'value' | 'onChange' | 'type'> {
  value: number | null | undefined;
  onChange: (cents: number) => void;
  allowEmpty?: boolean;
}

/**
 * Campo monetário em centavos com máscara estilo caixa eletrônico: o usuário
 * digita só números e o valor "anda" da direita para a esquerda (R$ 0,01 → R$ 0,12 → R$ 1,23).
 * Abre o teclado numérico no celular.
 */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, allowEmpty, placeholder = '0,00', ...props },
  ref,
) {
  const display = value === null || value === undefined || (allowEmpty && value === 0) ? '' : formatAmount(value);
  return (
    <Input
      ref={ref}
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      leading={<span className="text-[15px] font-medium text-ink-3">R$</span>}
      value={display}
      onChange={(e) => onChange(digitsToCents(e.target.value))}
      onFocus={(e) => {
        const el = e.currentTarget;
        requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
      }}
      className={cn('tabular', props.className)}
      {...props}
    />
  );
});

export const PercentInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'value' | 'onChange' | 'type'> & { value: number; onChange: (v: number) => void; max?: number }
>(function PercentInput({ value, onChange, max = 100, onFocus, onBlur, ...props }, ref) {
  // Enquanto digita, mostra o texto como está ("2," → "2,5"); fora do foco, o valor formatado.
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? (Number.isFinite(value) ? String(value).replace('.', ',') : '');
  return (
    <Input
      ref={ref}
      inputMode="decimal"
      value={shown}
      onFocus={(e) => {
        setText(shown);
        onFocus?.(e);
      }}
      onChange={(e) => {
        const [int = '', ...dec] = e.target.value.replace(/[^\d.,]/g, '').replace(/\./g, ',').split(',');
        const raw = dec.length ? `${int},${dec.join('').slice(0, 2)}` : int;
        setText(raw);
        const n = Number(raw.replace(',', '.'));
        onChange(Number.isFinite(n) ? Math.min(max, n) : 0);
      }}
      onBlur={(e) => {
        setText(null);
        onBlur?.(e);
      }}
      trailing={<span className="pr-2 text-[15px] font-medium text-ink-3">%</span>}
      {...props}
    />
  );
});

// ---------------------------------------------------------------- Máscaras BR
function maskedInput(format: (v: string) => string, maxDigits: number, inputMode: 'numeric' | 'tel') {
  return forwardRef<HTMLInputElement, Omit<InputProps, 'onChange' | 'value'> & { value: string | null | undefined; onChange: (v: string) => void }>(
    function Masked({ value, onChange, ...props }, ref) {
      return (
        <Input
          ref={ref}
          inputMode={inputMode}
          value={format(value ?? '')}
          onChange={(e) => onChange(onlyDigits(e.target.value).slice(0, maxDigits))}
          {...props}
        />
      );
    },
  );
}

/** Telefone/WhatsApp: armazena só dígitos (com DDD). */
export const PhoneInput = maskedInput((v) => (onlyDigits(v).length >= 10 ? formatPhone(v) : v), 11, 'tel');
export const DocumentInput = maskedInput((v) => formatDocument(v), 14, 'numeric');
export const CepInput = maskedInput((v) => formatCEP(v), 8, 'numeric');

// ---------------------------------------------------------------- Busca
export const SearchInput = forwardRef<
  HTMLInputElement,
  Omit<InputProps, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void; onClear?: () => void }
>(function SearchInput({ value, onChange, onClear, placeholder = 'Buscar', ...props }, ref) {
  return (
    <Input
      ref={ref}
      type="search"
      enterKeyHint="search"
      autoComplete="off"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      leading={<Search />}
      trailing={
        value ? (
          <button
            type="button"
            aria-label="Limpar busca"
            className="animate-fade-in rounded-full p-1.5 text-ink-3 transition-colors hover:bg-ink/[0.06] hover:text-ink"
            onClick={() => {
              onChange('');
              onClear?.();
            }}
          >
            <X className="size-4" />
          </button>
        ) : null
      }
      {...props}
    />
  );
});

// ---------------------------------------------------------------- Select nativo (melhor UX no celular)
const chevron = (hex: string) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23${hex}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`;

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean; placeholder?: string }>(function Select(
  { className, invalid, placeholder, children, style, ...props },
  ref,
) {
  return (
    <select
      ref={ref}
      className={cn(
        fieldBase,
        invalid || props['aria-invalid'] ? fieldError : fieldOk,
        'h-11 appearance-none bg-[length:18px] bg-[right_0.75rem_center] bg-no-repeat pr-10',
        'bg-[image:var(--dz-select-chevron)] dark:bg-[image:var(--dz-select-chevron-dark)]',
        className,
      )}
      style={{ ['--dz-select-chevron' as string]: chevron('686873'), ['--dz-select-chevron-dark' as string]: chevron('A9A9B7'), ...style }}
      {...props}
    >
      {placeholder ? (
        <option value="" disabled={props.required}>
          {placeholder}
        </option>
      ) : null}
      {children}
    </select>
  );
});

// ---------------------------------------------------------------- Switch
export function Switch({
  checked,
  onCheckedChange,
  disabled,
  id,
  'aria-label': ariaLabel,
  size = 'md',
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  size?: 'sm' | 'md';
}) {
  const reduce = useReducedMotion();
  const travel = size === 'md' ? 20 : 16;
  return (
    <RSwitch.Root
      id={id}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        'group relative inline-flex shrink-0 items-center rounded-full bg-ink/[0.16] p-0.5 transition-colors duration-200 ease-standard',
        'hover:bg-ink/[0.22] data-[state=checked]:bg-primary data-[state=checked]:hover:bg-primary-hover disabled:opacity-50',
        size === 'md' ? 'h-7 w-12' : 'h-5 w-9',
      )}
    >
      <RSwitch.Thumb asChild>
        <motion.span
          initial={false}
          animate={{ x: checked ? travel : 0 }}
          transition={reduce ? { duration: 0 } : springs.bouncy}
          className={cn(
            'block rounded-full bg-white shadow-[0_1px_2px_rgb(14_14_20/0.2),0_2px_6px_rgb(14_14_20/0.12)]',
            size === 'md' ? 'size-6' : 'size-4',
          )}
        />
      </RSwitch.Thumb>
    </RSwitch.Root>
  );
}

/** Linha de configuração com título, descrição e switch (padrão das telas de Configurações). */
export function SwitchRow({
  title,
  description,
  checked,
  onCheckedChange,
  disabled,
  badge,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  disabled?: boolean;
  badge?: ReactNode;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex items-center gap-4 px-4 py-3.5', className)}>
      <label htmlFor={id} className="min-w-0 flex-1 cursor-pointer">
        <span className="flex items-center gap-2 text-[15px] font-medium text-ink">
          {title}
          {badge}
        </span>
        {description ? <span className="mt-0.5 block text-sm text-ink-2">{description}</span> : null}
      </label>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}

// ---------------------------------------------------------------- Checkbox
export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  className,
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={cn('flex items-start gap-3', className)}>
      <RCheckbox.Root
        id={id}
        checked={checked}
        onCheckedChange={(v) => onCheckedChange(v === true)}
        disabled={disabled}
        className={cn(
          'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-[6px] border border-line-strong bg-surface shadow-control',
          'transition-[background-color,border-color,scale] duration-150 ease-standard hover:border-ink/30 active:scale-90',
          'data-[state=checked]:border-primary data-[state=checked]:bg-primary disabled:opacity-50',
        )}
      >
        <RCheckbox.Indicator className="animate-pop">
          <Check className="size-3.5 text-on-primary" strokeWidth={3} />
        </RCheckbox.Indicator>
      </RCheckbox.Root>
      {label ? (
        <label htmlFor={id} className="cursor-pointer text-[15px] leading-snug text-ink">
          {label}
          {description ? <span className="mt-0.5 block text-sm text-ink-2">{description}</span> : null}
        </label>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- Segmentado
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
  size = 'md',
  'aria-label': ariaLabel,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; icon?: ReactNode; disabled?: boolean }[];
  className?: string;
  size?: 'sm' | 'md';
  'aria-label'?: string;
}) {
  const layoutId = useId();
  const reduce = useReducedMotion();
  return (
    <div role="radiogroup" aria-label={ariaLabel} className={cn('inline-flex rounded-control bg-ink/[0.05] p-1 ring-1 ring-inset ring-line', className)}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'relative isolate inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] font-semibold transition-colors duration-150 disabled:opacity-40 [&_svg]:size-4',
              size === 'md' ? 'h-9 px-3 text-sm' : 'h-7 px-2.5 text-xs',
              active ? 'text-ink' : 'text-ink-2 hover:text-ink',
            )}
          >
            {active ? (
              <motion.span
                layoutId={`seg-${layoutId}`}
                transition={reduce ? { duration: 0 } : springs.snappy}
                aria-hidden="true"
                className="absolute inset-0 -z-10 rounded-[9px] bg-surface-raised shadow-[0_1px_2px_rgb(14_14_20/0.08),0_0_0_1px_var(--color-line)]"
              />
            ) : null}
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Opções grandes selecionáveis (formas de pagamento, tipo de entrega...). */
export function ChoiceCards<T extends string>({
  value,
  onChange,
  options,
  columns = 2,
  className,
}: {
  value: T | null;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; description?: ReactNode; icon?: ReactNode; disabled?: boolean }[];
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}) {
  // No celular, 3 ou 4 colunas viram 2: o último card de uma linha incompleta ocupa a linha toda.
  const cols = {
    1: 'grid-cols-1',
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3 max-sm:[&>*:last-child:nth-child(odd)]:col-span-2',
    4: 'grid-cols-2 sm:grid-cols-4 max-sm:[&>*:last-child:nth-child(odd)]:col-span-2',
  }[columns];
  return (
    <div role="radiogroup" className={cn('grid gap-2', cols, className)}>
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              'flex min-h-14 items-center gap-3 rounded-control border px-3.5 py-3 text-left',
              'transition-[border-color,background-color,box-shadow,scale] duration-150 ease-standard active:scale-[0.98] motion-reduce:active:scale-100 disabled:opacity-40 [&_svg]:size-5',
              active
                ? 'border-primary bg-primary-soft text-primary-strong ring-1 ring-primary'
                : 'border-line-strong bg-surface text-ink shadow-control hover:border-primary/50',
            )}
          >
            {o.icon ? <span className={cn('shrink-0', active ? 'text-primary-strong' : 'text-ink-2')}>{o.icon}</span> : null}
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold leading-tight">{o.label}</span>
              {o.description ? <span className={cn('mt-0.5 block text-xs', active ? 'text-primary-strong/85' : 'text-ink-2')}>{o.description}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------- Quantidade
export function QuantityStepper({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  size = 'md',
  className,
  label = 'Quantidade',
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  size?: 'sm' | 'md';
  className?: string;
  label?: string;
}) {
  const dec = () => onChange(Math.max(min, Math.round((value - step) * 1000) / 1000));
  const inc = () =>
    onChange(max !== undefined ? Math.min(max, Math.round((value + step) * 1000) / 1000) : Math.round((value + step) * 1000) / 1000);
  const btn = cn(
    'flex items-center justify-center rounded-full text-primary-strong transition-[background-color,scale] duration-150 hover:bg-primary-soft active:scale-90 disabled:opacity-30 motion-reduce:active:scale-100',
    size === 'md' ? 'size-9' : 'size-7',
  );
  return (
    <div
      role="group"
      aria-label={label}
      className={cn('inline-flex items-center gap-1 rounded-full border border-line-strong bg-surface p-0.5 shadow-control', className)}
    >
      <button type="button" className={btn} onClick={dec} disabled={value <= min} aria-label="Diminuir">
        <Minus className="size-4" />
      </button>
      <span className={cn('tabular min-w-8 text-center font-semibold', size === 'md' ? 'text-base' : 'text-sm')} aria-live="polite">
        {Number.isInteger(value) ? value : value.toLocaleString('pt-BR')}
      </span>
      <button type="button" className={btn} onClick={inc} disabled={max !== undefined && value >= max} aria-label="Aumentar">
        <Plus className="size-4" />
      </button>
    </div>
  );
}
